import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Pagination,
    CircularProgress,
    Alert,
    Grid,
    Button,
    Container
} from '@mui/material';
import {
    ArrowBack,
    VideoLibrary
} from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import PageContainer from '../layout/PageContainer';
import VideoPostCard from './VideoPostCard';

const VideoPostsList = () => {
    const navigate = useNavigate();
    const [videoPosts, setVideoPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Mock topics for now
    const mockTopics = ['Technology', 'Science', 'History', 'Literature', 'Business', 'Health'];

    const [selectedTopics, setSelectedTopics] = useState([]);

    useEffect(() => {
        const fetchVideoPosts = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await userRequest.get('/contentVideoPost', {
                params: { 
                    page, 
                    limit: 12
                }
            });
            
            if (response?.data?.videoPosts) {
                setVideoPosts(response.data.videoPosts);
                setTotalPages(response.data.totalPages || 1);
            } else {
                setVideoPosts([]);
                setTotalPages(1);
            }
            
        } catch (error) {
            console.error('Error fetching video posts:', error);
            
            if (error.response?.status === 403) {
                setError('You do not have permission to view video posts.');
            } else {
                setError('Failed to load video posts. Please try again.');
            }
        } finally {
            setLoading(false);
        }
        };
        
        fetchVideoPosts();
    }, [page]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleTopicToggle = (topic) => {
        setSelectedTopics(prev => 
            prev.includes(topic) 
                ? prev.filter(t => t !== topic)
                : [...prev, topic]
        );
    };

    return (
        <PageContainer>
            <Container maxWidth="lg" sx={{ py: 4, backgroundColor: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/profile')}
                    >
                        Back
                    </Button>
                    <Typography variant="h4" fontWeight="600" sx={{ flex: 1, textAlign: 'center' }}>
                        Video
                    </Typography>
                    <Box sx={{ width: 100 }} /> {/* Spacer for centering */}
                </Box>

                {/* Topic Buttons */}
                <Box sx={{ mb: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                        }}
                    >
                        {mockTopics.map((topic) => (
                            <Button
                                key={topic}
                                variant={selectedTopics.includes(topic) ? 'contained' : 'outlined'}
                                onClick={() => handleTopicToggle(topic)}
                                sx={{
                                    borderRadius: '20px',
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    boxShadow: selectedTopics.includes(topic) ? 3 : 0,
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {topic}
                            </Button>
                        ))}
                    </Box>
                </Box>

            {/* Error State */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Video Posts Grid */}
            {!loading && !error && (
                <>
                    {videoPosts.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <VideoLibrary sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                No video posts found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Try adjusting your filters or search terms.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={4} sx={{ mb: 4 }}>
                                {videoPosts.map((post) => (
                                    <Grid item xs={12} sm={6} md={4} key={post._id}>
                                        <VideoPostCard post={post} />
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={handlePageChange}
                                        color="primary"
                                        showFirstButton
                                        showLastButton
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}
            </Container>
        </PageContainer>
    );
};

export default VideoPostsList; 